import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  LuArrowDownWideNarrow,
  LuArrowUpDown,
  LuCalendarClock,
  LuDatabase,
  LuFilter,
  LuHash,
  LuPlus,
  LuSearch,
  LuUpload,
} from "react-icons/lu";
import { toast } from "react-toastify";

import ExportMenu from "../../components/importExport/ExportMenu";
import ImportModal from "../../components/importExport/ImportModal";
import ItemList from "../../components/items/ItemList";
import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import Pagination from "../../components/pagination";
import Button from "../../components/ui/Button";
import FilterSelect, { FilterOption } from "../../components/ui/FilterSelect";
import Input from "../../components/ui/Input";
import { sortLabelsOf } from "../../components/ui/SortableTableHead";
import cx from "../../components/ui/cx";
import {
  actionBarClass,
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardTitleClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  pageTitleClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchItemsListData,
  getItems,
  getTotalItems,
} from "../../features/items/itemsSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { DELETE } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { ItemDateBy, ItemSortBy, ItemSortOrder } from "../../types/item";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "આઈટમ્સ" },
];

const listKey = "items";

const dateByOptions: FilterOption<ItemDateBy>[] = [
  { value: "created_at", label: "બનાવ્યા તારીખ" },
  { value: "updated_at", label: "સુધાર્યા તારીખ" },
];

const sortByOptions: FilterOption<ItemSortBy>[] = [
  { value: "id", label: "ID" },
  { value: "name", label: "આઈટમ નામ" },
  { value: "category", label: "કેટેગરી" },
  { value: "sub_items_count", label: "S. Count" },
  { value: "created_at", label: "બનાવ્યા તારીખ" },
  { value: "updated_at", label: "સુધાર્યા તારીખ" },
];

const sortLabels = sortLabelsOf(sortByOptions);

const sortOrderOptions: FilterOption<ItemSortOrder>[] = [
  { value: "asc", label: "ચડતો ક્રમ" },
  { value: "desc", label: "ઉતરતો ક્રમ" },
];

const ItemsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const items = useAppSelector(getItems);
  const totalItems = useAppSelector(getTotalItems);
  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.itemsCreate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.itemsReport
  );
  // The import updates the items a file names by a saved code only for a user who may update items.
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.itemsUpdate
  );

  interface FilterFormInput {
    name: string;
    fromDate: string;
    toDate: string;
    codeFrom: string;
    codeTo: string;
    date_by: ItemDateBy;
    sort_by: ItemSortBy;
    sort_order: ItemSortOrder;
  }

  // The dates filter the date an item was added, newest first, until the user picks otherwise.
  const emptyFilters: FilterFormInput = {
    name: "",
    fromDate: "",
    toDate: "",
    codeFrom: "",
    codeTo: "",
    date_by: "created_at",
    sort_by: "id",
    sort_order: "desc",
  };

  // The filters in use when the user last left the list (to edit an item, say), else none.
  const savedFilters: FilterFormInput = savedList.filters || emptyFilters;

  const { handleSubmit, register, reset, getValues, setValue, watch } =
    useForm<FilterFormInput>({ defaultValues: savedFilters });

  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );
  const [showImport, setShowImport] = useState(false);
  // The order the list on screen was loaded in, which the table headers show. The filter's selects may hold an
  // order that isn't applied yet.
  const [appliedSort, setAppliedSort] = useState<{
    sortBy: ItemSortBy;
    sortOrder: ItemSortOrder;
  }>({ sortBy: savedFilters.sort_by, sortOrder: savedFilters.sort_order });

  const fetchId = useRef(0);

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleShowItemForm = () => {
    navigate(route.itemCreate);
  };

  const handleDeleteItem = async (id: number) => {
    setIsLoading(true);

    try {
      await DELETE(`/v1/admin/items/${id}`)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = items.length > 1 ? page : page - 1;
            if (page === pageNumber || pageNumber < 1) {
              fetchItemsList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            setIsLoading(false);

            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          setIsLoading(false);

          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      setIsLoading(false);

      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  // A new filter or import starts again from the first page.
  const showFirstPage = () => {
    if (page === 1) {
      fetchItemsList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleOnFilterSubmit = async () => {
    showFirstPage();
  };

  const handleClearFilter = () => {
    reset(emptyFilters);
    showFirstPage();
  };

  // Filter values shared by the list and the export.
  const filterParams = () => {
    const data = getValues();

    return {
      search: data?.name || null,
      fromDate: data?.fromDate || null,
      toDate: data?.toDate || null,
      codeFrom: data?.codeFrom?.trim() || null,
      codeTo: data?.codeTo?.trim() || null,
      date_by: data.date_by,
      sort_by: data.sort_by,
      sort_order: data.sort_order,
    };
  };

  const fetchItemsList = async (page: number, size: number) => {
    const currentFetch = ++fetchId.current;

    setIsLoading(true);

    const params = filterParams();

    dispatch(saveListState({ key: listKey, value: { filters: getValues() } }));
    setAppliedSort({ sortBy: params.sort_by, sortOrder: params.sort_order });

    dispatch(
      fetchItemsListData({
        page: page,
        size: size,
        ...params,
        // Sub items are managed on their parent's page, not listed here.
        main: true,
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / size)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  };

  // A table header sorts the list as the filter's ગોઠવો and ક્રમ do, and they show the same choice.
  const handleSort = (sortBy: ItemSortBy, sortOrder: ItemSortOrder) => {
    setValue("sort_by", sortBy);
    setValue("sort_order", sortOrder);
    showFirstPage();
  };

  // Coming back to the list (from a record's page, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({ key: listKey, value: { page, pageSize, showFilter } })
    );
  }, [dispatch, page, pageSize, showFilter]);

  useEffect(() => {
    fetchItemsList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>આઈટમ્સ</h1>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className={actionBarClass}>
          <div
            className={cx(
              "grid w-full gap-2 sm:ml-auto sm:flex sm:w-auto",
              canCreate ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            <Button
              variant="outline"
              onClick={() => setShowFilter(!showFilter)}
            >
              <LuFilter className="h-4 w-4" />
              ફિલ્ટર
            </Button>

            {canCreate && (
              <Button variant="outline" onClick={() => setShowImport(true)}>
                <LuUpload className="h-4 w-4" />
                ઇમ્પોર્ટ
              </Button>
            )}

            {canCreate && (
              <Button
                variant="primary"
                className="col-span-2"
                onClick={handleShowItemForm}
              >
                <LuPlus className="h-4 w-4" />
                નવી આઇટમ ઉમેરો
              </Button>
            )}
          </div>
        </div>

        {showFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>ફિલ્ટર</div>
            </div>

            <div className={cardContentClass}>
              <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={filterLabelClass}>શોધો</label>
                    <div className="relative">
                      <LuSearch className={fieldIconClass} />
                      <Input
                        placeholder="નામ દ્વારા શોધો"
                        className={fieldInputClass()}
                        {...register("name")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ થી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        placeholder="તારીખ દાખલ કરો"
                        className={fieldInputClass()}
                        {...register("fromDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ સુધી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        placeholder="તારીખ દાખલ કરો"
                        className={fieldInputClass()}
                        {...register("toDate")}
                      />
                    </div>
                  </div>

                  <FilterSelect
                    label="તારીખ ફિલ્ટર"
                    icon={LuCalendarClock}
                    placeholder="તારીખ સિલેક્ટ કરો"
                    options={dateByOptions}
                    value={watch("date_by")}
                    onChange={(value) => value && setValue("date_by", value)}
                  />

                  <div className="space-y-2">
                    <label className={filterLabelClass}>નામ (કોડ) થી</label>
                    <div className="relative">
                      <LuHash className={fieldIconClass} />
                      <Input
                        placeholder="દા.ત. AB-01"
                        className={fieldInputClass()}
                        {...register("codeFrom")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>નામ (કોડ) સુધી</label>
                    <div className="relative">
                      <LuHash className={fieldIconClass} />
                      <Input
                        placeholder="દા.ત. AB-20"
                        className={fieldInputClass()}
                        {...register("codeTo")}
                      />
                    </div>
                  </div>

                  <FilterSelect
                    label="ગોઠવો"
                    icon={LuArrowUpDown}
                    placeholder="ગોઠવણી સિલેક્ટ કરો"
                    options={sortByOptions}
                    value={watch("sort_by")}
                    onChange={(value) => value && setValue("sort_by", value)}
                  />

                  <FilterSelect
                    label="ક્રમ"
                    icon={LuArrowDownWideNarrow}
                    placeholder="ક્રમ સિલેક્ટ કરો"
                    options={sortOrderOptions}
                    value={watch("sort_order")}
                    onChange={(value) =>
                      value && setValue("sort_order", value)
                    }
                  />
                </div>

                <div className={filterFooterClass}>
                  <Button
                    type="button"
                    variant="outline"
                    className="max-sm:flex-1"
                    onClick={handleClearFilter}
                  >
                    ફિલ્ટર સાફ કરો
                  </Button>

                  {canReport && (
                    <ExportMenu
                      endpoint="/v1/admin/items/export"
                      getParams={filterParams}
                    />
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="max-sm:flex-1"
                  >
                    ફિલ્ટર લાગુ કરો
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ItemList
          items={items}
          pageNumber={page}
          pageSize={pageSize}
          isLoading={isLoading}
          sortBy={appliedSort.sortBy}
          sortOrder={appliedSort.sortOrder}
          onSort={handleSort}
          sortLabels={sortLabels}
          handleDeleteItem={handleDeleteItem}
        />

        {!isLoading && items.length > 0 && (
          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalItems}
            paginate={paginate}
            currentPage={page}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="આઈટમ"
          />
        )}
      </div>

      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        title="આઈટમ ઇમ્પોર્ટ કરો"
        endpoint="/v1/admin/items"
        help={[
          "કોલમ: કેટેગરી, નામ (કોડ), ડિસ્ક્રિપ્શન, મટીરીયલ, મુખ્ય આઈટમ, નંગ, સાઈઝ, કટીંગ લંબાઈ, કટીંગ ભાવ, ટર્નિંગ ભાવ, VMC ભાવ.",
          "કેટેગરી અને મટીરીયલ એપ માં ઉમેરેલા નામ થી જ લખો.",
          "ભાવ વૈકલ્પિક છે, ખાલી રાખી શકાય. મુખ્ય આઈટમ અને દરેક સબ આઈટમ ના ભાવ પોતપોતાની પંક્તિ માં લખો.",
          "કટીંગ લંબાઈ વૈકલ્પિક છે: એક નંગ કાપવા માટે જોઈતું મટીરીયલ, મટીરીયલ ની સાઈઝ ના યુનિટ માં (દા.ત. 96). સબ આઈટમ વાળી આઈટમ માં તે ખાલી રાખો.",
          "સાઈઝ વાળી આઈટમ ની દરેક સાઈઝ માટે અલગ પંક્તિ માં ફક્ત નામ (કોડ), સાઈઝ અને તે સાઈઝ ની કટીંગ લંબાઈ અને ભાવ લખો; આઈટમ ની પોતાની પંક્તિ માં સાઈઝ, કટીંગ લંબાઈ અને ભાવ ખાલી રાખો, અને તેનો કોડ લખેલો હોવો જોઈએ.",
          "સબ આઈટમ માટે મુખ્ય આઈટમ નો કોડ અને નંગ લખો. મુખ્ય આઈટમ આ જ ફાઇલ માં હોવી જોઈએ અને તેમાં મટીરીયલ ખાલી રાખો.",
          "સબ આઈટમ નો કોડ ખાલી હોય તો મુખ્ય આઈટમ ના કોડ પરથી આપમેળે બનશે (દા.ત. AB-02-01).",
          "મુખ્ય આઈટમ નો કોડ ખાલી હોય તો આગળનો કોડ આપમેળે બનશે, પણ આવી પંક્તિ દરેક ઇમ્પોર્ટ વખતે નવી આઈટમ બનાવે છે.",
          ...(canUpdate
            ? [
                "પહેલેથી ઉમેરેલા કોડ વાળી આઈટમ ફરીથી નહીં બને, ફાઇલ મુજબ સુધારાશે: એક્સપોર્ટ કરેલી ફાઇલ માં ફેરફાર કરીને ઇમ્પોર્ટ કરો.",
                "સુધારાતી આઈટમ માં ખાલી ખાનું ખાલી સેવ થશે, અને ફાઇલ માં ન હોય તે સબ આઈટમ અને સાઈઝ તેમાંથી કાઢી નાખવામાં આવશે. ફોટો બદલાતા નથી.",
                "આઈટમ તેના કોડ થી ઓળખાય છે: ફાઇલ માં બદલેલો કોડ નવી આઈટમ બનાવે છે, તેથી કોડ આઈટમ પેજ પર બદલો.",
              ]
            : [
                "પહેલેથી ઉમેરેલા કોડ વાળી આઈટમ તેની સબ આઈટમ સાથે છોડી દેવામાં આવશે.",
              ]),
        ]}
        onImported={showFirstPage}
      />
    </AppLayout>
  );
};
export default ItemsPage;
